/**
 * Профиль игрока: сводка, статистика, результат по дням, история розыгрышей, настройки.
 * Тот же экран администратор открывает для любого пользователя (/admin/users/:id) — тогда
 * вместо настроек показываются действия администратора (блокировка и разблокировка).
 */
import { useEffect, useState } from 'react';
import type { ProfileDetails } from '@mt/protocol';
import { api, useLoad } from '../../lib/api';
import { cr, crn, pts } from '../../lib/format';
import { Link } from '../../lib/router';
import { client, useApp } from '../../net/client';
import { ProviderIcon } from '../auth/ProviderIcon';
import { Dialog } from '../Dialog';
import { ago, Avatar, DayBars, dateTime, pct, signedCr, Tile, toneOf } from '../kit';
import { GameList } from './GameList';

type Tab = 'stats' | 'games' | 'settings';

export function ProfilePage({ userId }: { userId?: string }) {
  const admin = !!userId;
  const { data: p, error, reload, set } = useLoad(() => (userId ? api.admin.user(userId) : api.me()), [userId]);
  const [tab, setTab] = useState<Tab>('stats');
  const liveBalance = useApp((s) => s.profile?.balance);
  const stats = useApp((s) => s.profile?.stats.games);
  // после розыгрыша профиль в шапке обновляется по WebSocket — подтягиваем подробности
  useEffect(() => {
    if (!admin && stats !== undefined) reload();
  }, [stats, admin, reload]);

  if (error) return <div className="page"><p className="notice danger">{error}</p></div>;
  if (!p) return <div className="page center dim">Загрузка профиля…</div>;
  const balance = admin ? p.balance : (liveBalance ?? p.balance);

  return (
    <div className="page profile-page">
      {admin && (
        <nav className="crumbs">
          <Link to="/admin">Администрирование</Link> <span>/</span> <Link to="/admin?tab=users">Пользователи</Link> <span>/</span> <b>{p.name}</b>
        </nav>
      )}
      <ProfileHeader p={p} balance={balance} admin={admin} onChange={set} />

      <div className="segmented tabs" role="tablist">
        <button role="tab" aria-selected={tab === 'stats'} className={tab === 'stats' ? 'on' : ''} onClick={() => setTab('stats')}>
          Статистика
        </button>
        <button role="tab" aria-selected={tab === 'games'} className={tab === 'games' ? 'on' : ''} onClick={() => setTab('games')}>
          Розыгрыши · {p.stats.games}
        </button>
        {!admin && (
          <button role="tab" aria-selected={tab === 'settings'} className={tab === 'settings' ? 'on' : ''} onClick={() => setTab('settings')}>
            Настройки
          </button>
        )}
      </div>

      {tab === 'stats' && <StatsTab p={p} />}
      {tab === 'games' && (
        <section className="panel">
          <GameList
            mode="player"
            load={(o, l) => (admin ? api.admin.userGames(p.id, o, l) : api.myGames(o, l))}
            replayPath={(id) => (admin ? `/admin/games/${id}` : `/profile/games/${id}`)}
          />
        </section>
      )}
      {tab === 'settings' && !admin && <SettingsTab p={p} onChange={set} />}
    </div>
  );
}

function ProfileHeader({ p, balance, admin, onChange }: { p: ProfileDetails; balance: number; admin: boolean; onChange: (p: ProfileDetails) => void }) {
  const canRestore = useApp((s) => s.profile?.canRestore);
  const info = useApp((s) => s.info);
  const [banOpen, setBanOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const act = async (fn: () => Promise<ProfileDetails>) => {
    setBusy(true);
    try {
      onChange(await fn());
    } catch (e) {
      client.toast('error', (e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="panel profile-head">
      <Avatar id={p.id} name={p.name} size={64} bot={p.isBot} />
      <div className="profile-id">
        <h1>
          {p.name}
          {p.isBot && <em className="tag">бот</em>}
          {p.bannedAt && <em className="tag danger">заблокирован</em>}
          {p.online && <em className="tag ok">в сети</em>}
        </h1>
        <p className="muted-line">
          С нами с {dateTime(p.createdAt)} · последняя активность {p.online ? 'сейчас' : ago(p.lastSeenAt)}
          {admin && (
            <>
              {' '}
              · <code>{p.id}</code>
            </>
          )}
        </p>
        <div className="methods">
          {p.methods.login && <span className="chip-static">логин {p.methods.login}</span>}
          {p.methods.providers.map((m) => (
            <span key={m.id} className="chip-static">
              <ProviderIcon id={m.id} /> {m.display ?? m.email ?? m.id}
            </span>
          ))}
        </div>
        {p.bannedAt && (
          <p className="notice danger">
            Заблокирован {dateTime(p.bannedAt)}
            {p.banReason ? `: ${p.banReason}` : ''}
          </p>
        )}
      </div>
      <div className="profile-balance">
        <span className="dim small">Баланс</span>
        <b className="mono">{cr(balance)}</b>
        {!admin && canRestore && info && (
          <button className="btn tiny" onClick={() => client.restore()}>
            Восстановить {cr(info.startingBalance)}
          </button>
        )}
        {admin &&
          !p.isBot &&
          (p.bannedAt ? (
            <button className="btn tiny" disabled={busy} onClick={() => void act(() => api.admin.unban(p.id))}>
              Разблокировать
            </button>
          ) : (
            <button className="btn tiny danger-outline" disabled={busy} onClick={() => setBanOpen(true)}>
              Заблокировать
            </button>
          ))}
      </div>
      {banOpen && (
        <Dialog
          title={`Заблокировать ${p.name}`}
          tone="danger"
          confirmText="Заблокировать"
          onClose={() => setBanOpen(false)}
          onConfirm={() => {
            setBanOpen(false);
            void act(() => api.admin.ban(p.id, reason));
          }}
        >
          <p>Игрок будет отключён от игры и не сможет войти. В активном розыгрыше его ход истечёт по тайм-ауту, место освободится после розыгрыша.</p>
          <label className="field">
            Причина (видна игроку)
            <input value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} placeholder="например, нарушение правил" data-autofocus />
          </label>
        </Dialog>
      )}
    </section>
  );
}

function StatsTab({ p }: { p: ProfileDetails }) {
  const s = p.stats;
  const played = s.games - s.cancelled;
  return (
    <div className="stats-layout">
      <section className="panel">
        <h3 className="panel-title">Итоги</h3>
        <dl className="tiles">
          <Tile label="Розыгрышей" value={s.games} hint={`из них отменено: ${s.cancelled}`} />
          <Tile label="Винрейт" value={pct(s.winRate)} hint="доля розыгрышей с выплатой среди состоявшихся" />
          <Tile label="Итог" value={signedCr(s.net)} tone={toneOf(s.net)} hint="сумма чистых результатов, кр." />
          <Tile label="Лучший выигрыш" value={s.bestWin > 0 ? `+${crn(s.bestWin)}` : '—'} tone={s.bestWin > 0 ? 'up' : ''} />
          <Tile label="Верное направление" value={played ? pct(s.correctDirection / Math.max(1, played)) : '—'} />
          <Tile label="Средняя ошибка" value={s.avgError === null ? '—' : `${pts(s.avgError)} п.`} hint="при верном направлении" />
          <Tile label="Пасов" value={s.folds} />
          <Tile label="Восстановлений" value={s.restores} hint="бесплатное восстановление стартового баланса" />
        </dl>
      </section>
      <section className="panel">
        <h3 className="panel-title">Результат по дням</h3>
        <DayBars items={p.daily} value={(i) => p.daily[i]!.net} label="Чистый результат по дням" format={(v) => `${signedCr(v)} кр.`} />
        <p className="muted-line small">Последние {p.daily.length} дн. с игрой. Наведите на столбец, чтобы увидеть сумму.</p>
      </section>
    </div>
  );
}

function SettingsTab({ p, onChange }: { p: ProfileDetails; onChange: (p: ProfileDetails) => void }) {
  const info = useApp((s) => s.info);
  const [name, setName] = useState(p.name);
  const [login, setLogin] = useState(p.methods.login ?? '');
  const [current, setCurrent] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<ProfileDetails>, ok: string) => {
    setBusy(true);
    try {
      const r = await fn();
      onChange(r);
      client.patchProfile({ name: r.name, showStats: r.showStats });
      client.toast('info', ok);
      return true;
    } catch (e) {
      client.toast('error', (e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };
  const linked = new Set(p.methods.providers.map((m) => m.id));
  const available = info?.auth.providers ?? [];
  const link = async (id: string) => {
    try {
      location.assign((await api.oauthStart(id, true)).url);
    } catch (e) {
      client.toast('error', (e as Error).message);
    }
  };

  return (
    <div className="settings-layout">
      <section className="panel">
        <h3 className="panel-title">Видимость за столом</h3>
        <label className="switch-row">
          <input
            type="checkbox"
            checked={p.showStats}
            disabled={busy}
            onChange={(e) => {
              const on = e.target.checked;
              const prev = p;
              onChange({ ...p, showStats: on }); // сразу показываем выбор; при ошибке — откат
              void run(() => api.patchMe({ showStats: on }), on ? 'Винрейт и итог видны за столом' : 'Статистика скрыта').then((ok) => ok || onChange(prev));
            }}
          />
          <span>
            <b>Показывать винрейт и итог на моей плашке</b>
            <small>Другие игроки увидят их рядом с вашим именем в списке участников.</small>
          </span>
        </label>
        <div className="plaque-preview">
          <Avatar id={p.id} name={p.name} size={28} />
          <span className="who-name">{p.name}</span>
          {p.showStats ? (
            <span className="seat-stats">
              <span title="винрейт">{pct(p.stats.winRate)}</span>
              <span className={toneOf(p.stats.net)}>{signedCr(p.stats.net)}</span>
            </span>
          ) : (
            <span className="dim small">статистика скрыта</span>
          )}
        </div>
      </section>

      <section className="panel">
        <h3 className="panel-title">Имя за столом</h3>
        <form
          className="inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            void run(() => api.patchMe({ name }), 'Имя изменено');
          }}
        >
          <input value={name} maxLength={24} onChange={(e) => setName(e.target.value)} aria-label="Имя за столом" />
          <button className="btn" disabled={busy || name.trim() === p.name || name.trim().length < 2}>
            Сохранить
          </button>
        </form>
        <p className="muted-line small">В истории прошлых розыгрышей остаётся имя, с которым вы в них играли.</p>
      </section>

      <section className="panel">
        <h3 className="panel-title">{p.methods.login ? 'Смена пароля' : 'Вход по логину и паролю'}</h3>
        <form
          className="stack-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (pw !== pw2) return client.toast('error', 'пароли не совпадают');
            void run(() => api.setPassword({ login: p.methods.login ? undefined : login, current: p.methods.login ? current : undefined, password: pw }), 'Пароль сохранён').then((ok) => {
              if (ok) (setCurrent(''), setPw(''), setPw2(''));
            });
          }}
        >
          {!p.methods.login && (
            <label className="field">
              Логин
              <input autoComplete="username" value={login} maxLength={32} onChange={(e) => setLogin(e.target.value)} />
            </label>
          )}
          {p.methods.login && (
            <label className="field">
              Текущий пароль
              <input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
            </label>
          )}
          <label className="field">
            Новый пароль
            <input type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} />
          </label>
          <label className="field">
            Новый пароль ещё раз
            <input type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} />
          </label>
          <button className="btn primary" disabled={busy || pw.length < 8 || !pw2 || (p.methods.login ? !current : login.trim().length < 3)}>
            Сохранить пароль
          </button>
        </form>
      </section>

      <section className="panel">
        <h3 className="panel-title">Вход через сервисы</h3>
        {available.length === 0 && p.methods.providers.length === 0 && <p className="muted-line">На сервере не настроен вход через внешние сервисы.</p>}
        <ul className="provider-list">
          {p.methods.providers.map((m) => (
            <li key={m.id}>
              <ProviderIcon id={m.id} />
              <span>
                <b>{available.find((a) => a.id === m.id)?.title ?? m.id}</b>
                <small>
                  {m.display ?? ''} {m.email ? `· ${m.email}` : ''} · с {dateTime(m.linkedAt)}
                </small>
              </span>
              <button className="btn tiny ghost" disabled={busy} onClick={() => void run(() => api.unlink(m.id), 'Сервис отвязан')}>
                Отвязать
              </button>
            </li>
          ))}
          {available
            .filter((a) => !linked.has(a.id))
            .map((a) => (
              <li key={a.id}>
                <ProviderIcon id={a.id} />
                <span>
                  <b>{a.title}</b>
                  <small>не привязан</small>
                </span>
                <button className="btn tiny" onClick={() => void link(a.id)}>
                  Привязать
                </button>
              </li>
            ))}
        </ul>
      </section>
    </div>
  );
}
